import React, { useState, useMemo } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MoodboardProvider } from './contexts/MoodboardContext'; // Import MoodboardProvider
import { AuthPage } from './pages/AuthPage';
import { Dashboard } from './pages/Dashboard';
import { Moodboard } from './pages/Moodboard';
import { AITouchup } from './pages/AITouchup';
import { AICleanup } from './pages/AICleanup';
import { AIRemoveFurniture } from './pages/AIRemoveFurniture';
import AdminDashboard from './pages/AdminDashboard';
import { YourGallery } from './pages/YourGallery';
import { Settings } from './pages/Settings';
import { CustomerSupportChat } from './components/CustomerSupportChat';
import { ChatButton } from './components/ChatButton';



type PageType = 'dashboard' | 'moodboard' | 'your-gallery' | 'ai-touchup' | 'ai-cleanup' | 'ai-remove-furniture' | 'admin' | 'settings';

const AppContent: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const [currentPage, setCurrentPage] = useState<PageType>('dashboard');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Memoize token balance to prevent frequent re-renders
  const tokenBalance = useMemo(() => user?.tokens || 0, [user?.tokens]);
  const isAdmin = useMemo(() => Boolean(user?.is_admin), [user?.is_admin]);

  const handleSignOut = async () => {
    try {
      await signOut();
      setCurrentPage('dashboard'); // Reset to dashboard on sign out
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <header className="bg-white shadow">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            {/* Logo and Navigation */}
            <div className="flex items-center space-x-8">
              <button
                onClick={() => setCurrentPage('dashboard')}
                className="flex items-center focus:outline-none"
                aria-label="Go to Dashboard"
              >
                <img 
                  src="/assets/renovirai-logo.png" 
                  alt="RenovirAI" 
                  className="h-10 w-auto cursor-pointer"
                />
              </button>
              <nav className="hidden md:flex space-x-6">
                <button
                  onClick={() => setCurrentPage('your-gallery')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'your-gallery'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🖼️ Your Gallery
                </button>
                <button
                  onClick={() => setCurrentPage('moodboard')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'moodboard'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🎨 Moodboard
                </button>
                <button
                  onClick={() => setCurrentPage('ai-touchup')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'ai-touchup'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🖌️ Edit
                </button>
                <button
                  onClick={() => setCurrentPage('ai-cleanup')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'ai-cleanup'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🚀 Restyle
                </button>
                <button
                  onClick={() => setCurrentPage('ai-remove-furniture')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'ai-remove-furniture'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🪑 Remove Furniture
                </button>

                {isAdmin && (
                  <button
                    onClick={() => setCurrentPage('admin')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentPage === 'admin'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Admin
                  </button>
                )}
              </nav>
            </div>

            {/* User Info and Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="bg-blue-100 px-3 py-1 rounded-full">
                <span className="text-blue-800 font-medium text-sm">
                  🪙 {tokenBalance} tokens
                </span>
              </div>
              <button
                onClick={() => setCurrentPage('settings')}
                className="text-sm text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
              >
                {user?.email}
              </button>
              <button
                onClick={handleSignOut}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Sign Out
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-500 hover:text-gray-700 focus:outline-none">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden">
            <nav className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <button onClick={() => { setCurrentPage('your-gallery'); setIsMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">🖼️ Your Gallery</button>
              <button onClick={() => { setCurrentPage('moodboard'); setIsMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">🎨 Moodboard</button>
              <button onClick={() => { setCurrentPage('ai-touchup'); setIsMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">🖌️ Edit</button>
              <button onClick={() => { setCurrentPage('ai-cleanup'); setIsMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">🚀 Restyle</button>
              <button onClick={() => { setCurrentPage('ai-remove-furniture'); setIsMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">🪑 Remove Furniture</button>

              {isAdmin && (
                <button onClick={() => { setCurrentPage('admin'); setIsMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">Admin</button>
              )}
            </nav>
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center px-5">
                <div className="ml-3">
                  <button 
                    onClick={() => { setCurrentPage('settings'); setIsMenuOpen(false); }}
                    className="text-base font-medium text-gray-800 hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    {user?.email}
                  </button>
                  <div className="text-sm font-medium text-gray-500">🪙 {tokenBalance} tokens</div>
                </div>
              </div>
              <div className="mt-3 px-2 space-y-1">
                <button onClick={() => { handleSignOut(); setIsMenuOpen(false); }} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100">Sign out</button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Page Content */}
      <main>
        {currentPage === 'dashboard' && (
          <Dashboard onNavigate={(page) => setCurrentPage(page)} />
        )}
        {currentPage === 'moodboard' && <Moodboard />}
        {currentPage === 'your-gallery' && <YourGallery />}
        {currentPage === 'ai-touchup' && <AITouchup />}
        {currentPage === 'ai-cleanup' && <AICleanup />}
        {currentPage === 'ai-remove-furniture' && <AIRemoveFurniture />}
        {currentPage === 'admin' && isAdmin && <AdminDashboard />}
        {currentPage === 'settings' && <Settings />}

      </main>

      {/* Chat Support */}
      <ChatButton onClick={() => setIsChatOpen(true)} />
      <CustomerSupportChat 
        isOpen={isChatOpen} 
        onClose={() => setIsChatOpen(false)} 
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <MoodboardProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </MoodboardProvider>
  );
};

export default App; 